import { OperationCategoryEnumFR } from '@app/models/api/enums/OperationCategoryEnum';
import { OperationStatusEnumFR } from '@app/models/api/enums/OperationStatusEnum';
import { OperationFinancialInstrumentEnumFR } from '@app/models/api/enums/OperationFinancialInstrumentEnum';
import type { FiltersGroup } from '@app/types/Filters';
import { OperationBenefitEnumFR } from '@app/models/api/enums/OperationBenefitEnum';
import { get } from 'svelte/store';
import { t } from 'svelte-i18n';
import { FiltersTypeEnum } from '@app/models/api/enums/FiltersTypeEnum';
import { OperationRegulationsEnum } from '@app/models/api/enums/OperationRegulationsEnum';

export const getOperationsFilters = (
	regulation: OperationRegulationsEnum,
	query?: URLSearchParams
): FiltersGroup[] => {
	const isPSFP = regulation === OperationRegulationsEnum.PSFP;
	const $t = get(t);

	const isQueryActive = (key: string, field: string) => {
		return query?.has(key) && query?.get(key)?.includes(field);
	};

	const categoriesFilter = {
		value: 'categories', // Use to match the API query value
		label: $t('projectsPage.filtersCategories.categories'),
		type: FiltersTypeEnum.CHIP,
		filters: [
			{
				value: OperationCategoryEnumFR.IMMOBILIER,
				label: $t('realEstate'),
				color: 'real-estate',
				checked: isQueryActive('categories', OperationCategoryEnumFR.IMMOBILIER),
				classNames: 'flex justify-center uppercase'
			},
			{
				value: OperationCategoryEnumFR.IMPACT_NATURE,
				label: $t('energyTransition'),
				color: 'energy-transition',
				checked: isQueryActive('categories', OperationCategoryEnumFR.IMPACT_NATURE),
				classNames: 'flex justify-center uppercase'
			},
			{
				value: OperationCategoryEnumFR.SANTE,
				label: $t('health'),
				color: 'health',
				checked: isQueryActive('categories', OperationCategoryEnumFR.SANTE),
				classNames: 'flex justify-center uppercase'
			},
			{
				value: OperationCategoryEnumFR.TECH_INNOVATION,
				label: $t('techAndInnovation'),
				color: 'funds',
				checked: isQueryActive('categories', OperationCategoryEnumFR.TECH_INNOVATION),
				classNames: 'flex justify-center uppercase'
			}
		]
	};

	const statusFilter = {
		value: 'status',
		type: FiltersTypeEnum.SELECT,
		label: $t('projectsPage.filtersCategories.status'),
		filters: [
			{
				value: query ? query.get($t('filters.status')) : undefined,
				options: [
					{
						value: OperationStatusEnumFR.EN_FINANCEMENT,
						label: $t('operation.status.funding')
					},
					{
						value: OperationStatusEnumFR.FINANCE,
						label: $t('operation.status.funded')
					},
					{
						value: OperationStatusEnumFR.SORTIE,
						label: $t('operation.status.refunded')
					},
					{
						value: OperationStatusEnumFR.EN_VOTE,
						label: $t('operation.status.vote')
					}
				]
			}
		]
	};

	const financialInstrumentsFilter = {
		value: 'instruments',
		type: FiltersTypeEnum.SELECT,
		label: $t('projectsPage.filtersCategories.instruments'),
		filters: [
			{
				value: query ? query.get($t('filters.instruments')) : undefined,
				options: [
					{
						value: OperationFinancialInstrumentEnumFR.ACTIONS,
						label: $t('operation.instruments.stocks')
					},
					{
						value: OperationFinancialInstrumentEnumFR.OBLIGATIONS,
						label: $t('operation.instruments.bonds')
					},
					{
						value: OperationFinancialInstrumentEnumFR.TITRES_PARTICIPATIFS,
						label: $t('operation.instruments.equity-securities')
					}
				]
			}
		]
	};

	const benefitsFilter = {
		value: 'benefits',
		type: FiltersTypeEnum.SELECT,
		label: $t('projectsPage.filtersCategories.benefits'),
		filters: [
			{
				value: query ? query.get($t('filters.benefits')) : undefined,
				options: [
					{
						value: OperationBenefitEnumFR.PEA,
						label: $t('operation.benefits.shareSavingsPlan')
					},
					{
						value: OperationBenefitEnumFR.IR,
						label: $t('operation.benefits.incomeTax')
					},
					{
						value: OperationBenefitEnumFR.RENDEMENT,
						label: $t('operation.benefits.return')
					},
					{
						value: OperationBenefitEnumFR.APPORT_CESSION,
						label: $t('operation.benefits.contributionCession')
					}
				]
			}
		]
	};

	if (isPSFP) {
		return [categoriesFilter, statusFilter, financialInstrumentsFilter, benefitsFilter];
	} else {
		return [statusFilter, benefitsFilter];
	}
};
